import * as types from '../../actions/actiontypes';

import {
	AnnotationStatus,
	ExcludedClientRequestStatusForSyncingCommentsInExternalExternalApplication,
	HttpStatusCodes
} from '../../util/uiconstants';
import {isEmpty, orderBy} from 'lodash';

import initialState from '../initialstate';
import moment from 'moment';
import uuidv4 from 'uuid/v4';

/**
 * This reducer function handles all the actions related to the top-level objects.
 * @param {Object} state - A portion of the overall Redux state, specifically the
 *      store.<%=names.camel%> object, representing the current data.
 * @param {Object} action - Action object containing type and other values
 * @return {*} current state or array of language objects
 */
export default function clientRequestCommentReducer(state = initialState.clientRequestComments, action) {
	switch (action.type) {
		case types.GET_CLIENT_REQUESTS_COMMENTS: {
			if (!isEmpty(action.response) && HttpStatusCodes.Ok === action.responseObj.status) {
				const history = [...action.response.history];
				if (action.isFromModal) {
					const comments = action.response.comments?.map((comment) => {
						return {
							...comment,
							show: true,
							replies: comment?.replies?.map((reply) => {
								return {
									...reply,
									show: true
								};
							})
						};
					});

					return {
						...state,
						modal: {
							history: sortComments(history),
							comments: sortComments(comments)
						}
					};
				} else {
					return {
						...state,
						details: {
							history: sortComments(history),
							comments: sortComments([...action.response.comments])
						}
					};
				}
			}
			return state;
		}
		case types.CLEAN_CLIENT_REQUESTS_COMMENTS: {
			if (action.isFromModal) {
				return {...state, modal: initialState.clientRequestComments.modal};
			} else {
				return {...state, details: initialState.clientRequestComments.details};
			}
		}
		case types.CREATE_CLIENT_REQUEST_COMMENT: {
			if (!isEmpty(action.response) && HttpStatusCodes.Created === action.responseObj.status) {
				return addCommentReply(state, 'details', action.response.parentComment, action.response);
			}

			if (action.isFromModal) {
				const noteId = uuidv4();
				const addComment = {
					id: noteId,
					annotationId: noteId,
					show: true,
					replies: [],
					createDate: moment.utc().format(),
					parentComment: action.data.parentCommentId,
					...action.data
				};

				return addCommentReply(state, 'modal', action.data.parentCommentId, addComment);
			}

			return state;
		}
		case types.DELETE_CLIENT_REQUESTS_COMMENT: {
			if (action.responseObj?.status === HttpStatusCodes.NoContent) {
				const history = [...state.details.history];
				if (
					action.clientRequestStatusId &&
					!ExcludedClientRequestStatusForSyncingCommentsInExternalExternalApplication.includes(
						action.clientRequestStatusId
					) &&
					action.commentId !== undefined &&
					action.commentId !== null
				) {
					const clonedState = _.cloneDeep(state);
					let commentSynced = clonedState.details.comments.find((c) => c.annotationId === action.commentId);
					if (commentSynced) {
						commentSynced.isRetry = false;
						commentSynced.statusId = AnnotationStatus.ContentRemoved;
						commentSynced.replies = [];
						commentSynced.modifiedDate = new Date();
					} else {
						const replies = _.flatMap(clonedState.details.comments.map((c) => c.replies));
						let reply = replies.find((r) => r.annotationId === action.commentId);
						reply.statusId = AnnotationStatus.ContentRemoved;
						reply.isRetry = false;
						reply.modifiedDate = new Date();
					}

					return {
						...state,
						details: {
							...state.details,
							comments: clonedState.details.comments,
							history: sortComments(history)
						}
					};
				}
				return {
					...state,
					details: {
						history: sortComments(history),
						comments: [...state.details.comments]
							.filter((comment) => comment.annotationId !== action.commentId)
							.map((comment) => {
								return {...comment, replies: comment.replies.filter((item) => item.annotationId !== action.commentId)};
							})
					}
				};
			}

			if (action.isFromModal) {
				return {
					...state,
					modal: {
						history: sortComments(history),
						comments: orderBy(
							[...state.modal.comments]
								.filter((c) => c.annotationId !== action.commentId || Number.isInteger(c.annotationId))
								.map((comment) => {
									if (comment.annotationId === action.commentId && Number.isInteger(comment.annotationId)) {
										return {
											...comment,
											show: false
										};
									}

									return {
										...comment,
										replies: orderBy(
											comment.replies
												.filter((item) => item.annotationId !== action.commentId || Number.isInteger(item.annotationId))
												.map((reply) => {
													if (reply.annotationId === action.commentId && Number.isInteger(reply.annotationId)) {
														return {
															...reply,
															show: false
														};
													}
													return reply;
												}),
											'createDate',
											'desc'
										)
									};
								}),
							'createDate',
							'desc'
						)
					}
				};
			}

			return state;
		}
		case types.RETRY_COMMENT_SYNC: {
			if (HttpStatusCodes.NoContent == action.responseObj?.status) {
				const clonedState = _.cloneDeep(state);
				if (action.meta.api.data.parentCommentId !== undefined && action.meta.api.data.parentCommentId !== null) {
					const replies = _.flatMap(clonedState.details.comments.map((c) => c.replies));
					let reply = replies.find((r) => r.annotationId === action.meta.api.data.commentId);
					reply.isRetry = false;
				} else {
					let commentSynced = clonedState.details.comments.find(
						(c) => c.annotationId === action.meta.api.data.commentId
					);
					commentSynced.isRetry = false;
				}

				return {
					...state,
					details: {
						...state.details,
						comments: clonedState.details.comments
					}
				};
			}
		}
		default:
			return state;
	}
}

const addCommentReply = (state, propName, parentCommentId, newNode) => {
	if (parentCommentId) {
		let newComments = [...state[propName].comments];

		let parentComment = {...newComments.find((item) => item.annotationId === parentCommentId)};
		parentComment.replies = [...parentComment.replies, newNode];

		const commentAdded = orderBy(
			[...newComments.filter((comment) => comment.annotationId !== parentCommentId), parentComment],
			'createDate',
			'asc'
		);

		return {
			...state,
			[propName]: {
				history: state[propName].history,
				comments: commentAdded
			}
		};
	} else {
		//Inserting in the bottom since is the last comment added
		return {
			...state,
			[propName]: {history: state[propName].history, comments: [...state[propName].comments, newNode]}
		};
	}
};

const sortComments = (comments) => {
	return _.orderBy(comments, 'createDate', 'asc');
};
